import type { ReactNode } from 'react';
import { Card, Group, Stack, Table, Text } from '@mantine/core';
import type { MantineSpacing } from '@mantine/core';
import { useViewport } from '../hooks/useViewport';

/**
 * Where a column goes on a handheld card. Defaults to a labelled row.
 *
 *   title     the headline — the thing somebody scans, searches or reads first
 *   subtitle  under the headline, unlabelled
 *   aside     right of the headline: a status badge, a count, a total
 *   row       a labelled row in the card body
 *   hidden    dropped on a phone; not worth the vertical space
 *
 * One axis rather than four booleans, because the flags were never independent: a column is in
 * exactly one place on the card, and a set of booleans lets a screen ask for two at once.
 */
export type CardSlot = 'title' | 'subtitle' | 'aside' | 'row' | 'hidden';

export interface DataColumn<T> {
  /** Stable identity: the React key for both the header cell and the card row. */
  key: string;
  /**
   * The header text, and the label beside the value on a card. The card view is the reason this is
   * data rather than JSX in a `<Table.Th>` — two hand-written layouts drift, and the label is the
   * first thing to drift.
   */
  label?: string;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right';
  /** Column width on the table. Ignored on a card. */
  width?: number;
  card?: CardSlot;
}

interface Props<T> {
  rows: T[];
  columns: DataColumn<T>[];
  rowKey: (row: T) => string;
  /** The width below which the TABLE scrolls sideways. Irrelevant to the card view. */
  minWidth: number;
  /** Rendered in a trailing cell on the table, and in the card's footer. */
  actions?: (row: T) => ReactNode;
  /** Width reserved for the actions cell on the table. */
  actionsWidth?: number;
  striped?: boolean;
  highlightOnHover?: boolean;
  stickyHeader?: boolean;
  verticalSpacing?: MantineSpacing;
  horizontalSpacing?: MantineSpacing;
}

/**
 * A list of records: a real table on a tablet or a desktop, a stack of cards on a phone.
 *
 * WHY THIS EXISTS
 * Every list screen wrapped a `Table` in `Table.ScrollContainer` with a `minWidth` between 520 and
 * 1100. On a 430px phone that is a table you drag sideways to read — you cannot see an item's
 * number and its quantity at the same time, which is the only reason to look at the row. The
 * sponsor found this on an iPhone; `useViewport` had existed since M1 for exactly this purpose with
 * `AppModal` as its only consumer.
 *
 * WHY COLUMNS ARE DATA
 * The alternative is each screen writing its rows twice — once as `<Table.Tr>`, once as a card —
 * and 16 screens keeping two layouts in step. They would not. Declaring the columns once means the
 * card's labels ARE the header's labels, so a renamed column cannot be right in one view and stale
 * in the other.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * No empty state and no loading state. Every screen already decides those, and most say something
 * specific worth keeping ("Nothing is expecting goods. Switch to All orders…"). A component that
 * owned them would flatten every one of those into the same shrug.
 *
 * NOT FOR EVERY TABLE. A grid whose point is reading ACROSS the columns — Planning's stratification
 * comparison — belongs in a scroll container, because cards destroy the comparison that is the
 * whole reason it is a grid.
 */
export function ResponsiveTable<T>({
  rows,
  columns,
  rowKey,
  minWidth,
  actions,
  actionsWidth = 90,
  striped,
  highlightOnHover,
  stickyHeader,
  verticalSpacing,
  horizontalSpacing,
}: Props<T>) {
  const formFactor = useViewport();

  if (formFactor === 'handheld') {
    // Falls back to the first column so a screen that declares no title still reads as a card
    // rather than a labelled row with nothing above it.
    const title = columns.find((c) => c.card === 'title') ?? columns[0];
    const subtitles = columns.filter((c) => c.card === 'subtitle');
    const asides = columns.filter((c) => c.card === 'aside');
    const bodyRows = columns.filter((c) => c !== title && (c.card ?? 'row') === 'row');

    return (
      <Stack gap="sm">
        {rows.map((row) => (
          <Card key={rowKey(row)} withBorder padding="sm">
            <Stack gap="xs">
              <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text fw={600} style={{ wordBreak: 'break-word' }}>
                    {title?.render(row)}
                  </Text>
                  {subtitles.map((c) => (
                    <Text key={c.key} size="sm" c="dimmed">
                      {c.render(row)}
                    </Text>
                  ))}
                </Stack>
                {asides.length > 0 && (
                  <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                    {asides.map((c) => (
                      <div key={c.key}>{c.render(row)}</div>
                    ))}
                  </Group>
                )}
              </Group>

              {bodyRows.length > 0 && (
                <Stack gap={4}>
                  {bodyRows.map((c) => (
                    <Group key={c.key} justify="space-between" wrap="nowrap" gap="md">
                      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                        {c.label}
                      </Text>
                      <Text size="sm" ta="right" style={{ minWidth: 0, wordBreak: 'break-word' }}>
                        {c.render(row)}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              )}

              {actions && (
                // --ai-size cascades into every Mantine ActionIcon inside, so the 28px icons the
                // table uses become 44px here — the iOS minimum — without each screen passing a
                // size it would then have to keep in step with the table's.
                <Group
                  justify="flex-end"
                  gap="xs"
                  wrap="nowrap"
                  style={{ '--ai-size': '2.75rem' } as React.CSSProperties}
                >
                  {actions(row)}
                </Group>
              )}
            </Stack>
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <Table.ScrollContainer minWidth={minWidth}>
      <Table
        striped={striped}
        highlightOnHover={highlightOnHover}
        stickyHeader={stickyHeader}
        verticalSpacing={verticalSpacing}
        horizontalSpacing={horizontalSpacing}
      >
        <Table.Thead>
          <Table.Tr>
            {columns.map((c) => (
              <Table.Th key={c.key} w={c.width} ta={c.align}>
                {c.label}
              </Table.Th>
            ))}
            {actions && <Table.Th w={actionsWidth} />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={rowKey(row)}>
              {columns.map((c) => (
                <Table.Td key={c.key} ta={c.align}>
                  {c.render(row)}
                </Table.Td>
              ))}
              {actions && (
                <Table.Td>
                  <Group gap={4} justify="flex-end" wrap="nowrap">
                    {actions(row)}
                  </Group>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
